import { useEffect, useId, useRef, type ReactNode, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';
import { trackSheet } from './openSheets';
import styles from './BottomSheet.module.css';

export interface BottomSheetProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
}

const CLOSE_DRAG_PX = 80;

/**
 * Replaces modal dialogs (DESIGN_SYSTEM §3, §5). Drag handle and swipe down
 * to close, with a visible Close button as the non-gesture way. The app
 * behind is made inert while the sheet is open.
 */
export function BottomSheet({ open, title, onClose, children }: BottomSheetProps) {
  const titleId = useId();
  const sheetRef = useRef<HTMLElement>(null);
  const drag = useRef<{ y: number; dy: number } | null>(null);
  // Callers pass a fresh onClose on every render; the effect below must not re-run for that, or it would take
  // focus back from a field being typed in (the keyboard closed on every keystroke, DECISIONS 2026-10-08)
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const untrack = trackSheet();
    const app = document.getElementById('root');
    app?.setAttribute('inert', '');
    sheetRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      app?.removeAttribute('inert');
      untrack();
      previous?.focus();
    };
  }, [open]);

  if (!open) return null;

  // Close follows the finger (DESIGN_SYSTEM §7)
  const onPointerDown = (e: PointerEvent) => {
    // Capturing would retarget the click away from the Close button.
    if ((e.target as Element).closest('button')) return;
    drag.current = { y: e.clientY, dy: 0 };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent) => {
    if (!drag.current || !sheetRef.current) return;
    drag.current.dy = Math.max(0, e.clientY - drag.current.y);
    sheetRef.current.style.transform = `translateY(${drag.current.dy}px)`;
  };
  const onPointerUp = () => {
    if (!drag.current || !sheetRef.current) return;
    const { dy } = drag.current;
    drag.current = null;
    sheetRef.current.style.transform = '';
    if (dy > CLOSE_DRAG_PX) onClose();
  };

  return createPortal(
    <>
      <div className={styles.scrim} onClick={onClose} aria-hidden="true" />
      <section ref={sheetRef} className={styles.sheet} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}>
        <div
          className={styles.head}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <span className={styles.handle} aria-hidden="true" />
          <div className={styles.titleRow}>
            <h2 id={titleId} className="t-section">{title}</h2>
            <Button variant="tertiary" onClick={onClose}>Close</Button>
          </div>
        </div>
        <div className={styles.body}>{children}</div>
      </section>
    </>,
    document.body,
  );
}
